import React, { useEffect, useRef, useMemo, useState } from "react";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { useNavigate } from "react-router-dom";

// ✅ APIs
import {
  getStripeProducts,
  updateExistingPatient,
  validateCoupon,
  submitIntakeForm,
} from "../../services/api";

// Images (keep yours)
import SemaglutideBottle from "../../assets/images/claya-semaglutide-vial-2.png";
import SemaglutideOrgBottle from "../../assets/images/claya-semaglutide-vial-2-og.png";
import TirezepatideBottle from "../../assets/images/claya-tirzepatide-vial-2.png";
import OralSemaglutideImg from "../../assets/images/OralSemaglutideImg.png";
import OralTirzepatideImg from "../../assets/images//OralTirzepatideImg.png";
import ClayaPhonePencil from "../../assets/images/claya_phone.png";
import DoctorLogo from "../../assets/images/doctor-pic.png";
import semaIconBadge from "../../assets/images/sema-icon-badge.png";
import BackedBy from "../../assets/images/logos.png";
import SemaIconTrophy from "../../assets/images/sema-icon-trophy.png";
import SemaIconDown from "../../assets/images/sema-icon-down.png";
import SemaIconRing from "../../assets/images/sema-icon-ring.png";
import SemaIconScale from "../../assets/images/sema-icon-scale.png";
import TrustBadge from "../../assets/images/trustBadge.png";
import Ozempic from "../../assets/images/ozempic.png";
import Clayahfsafsa from "../../assets/images/claya-hfsafsa.png";
import SafetyIcon from "../../assets/images/claya-safety-icon.png";

import padlockIcon from "../../assets/images/padlock.png";
import lockIcon from "../../assets/images/lockIcon.png";
import cvcIcon from "../../assets/images/cvcIcon.png";
import creditCardIcon from "../../assets/images/credit-card-icons.png";
import Alert from "../../assets/images/alert.png";

declare global {
  interface Window {
    EF?: {
      click: (params: {
        offer_id: string | null;
        affiliate_id?: string | null;
        sub1?: string | null;
        sub2?: string | null;
        sub3?: string | null;
        sub4?: string | null;
        sub5?: string | null;
        uid?: string | null;
        source_id?: string | null;
        transaction_id?: string | null;
      }) => void;
      conversion: (params: {
        offer_id: number;
        amount?: number;
        affiliate_id?: string;
      }) => void;
    };
    fbq: (...args: any[]) => void;
    trackdesk?: any;
  }
}

type MedChoice = "semaglutide" | "tirzepatide";
type OptionType = "injections" | "tablets";
type ApiMedType = "injectable" | "oral";

type StripeProduct = {
  id: string; // price id
  unit_amount: number; // cents
  active: boolean;
  type: string; // "recurring"
  recurring?: { interval_count: number };
  product?: string; // ✅ REQUIRED for validateCoupon(product_id)
  name?: string;
};

type FormState = {
  fullName: string;
  email: string;

  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;

  useDifferentBilling: boolean;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingState: string;
  billingZip: string;

  country: "US" | "";
  postcode: string;

  agreeTerms: boolean;
};

const DEFAULT_FORM: FormState = {
  fullName: "",
  email: "",

  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone: "",

  useDifferentBilling: false,
  billingAddress1: "",
  billingAddress2: "",
  billingCity: "",
  billingState: "",
  billingZip: "",

  country: "US",
  postcode: "",

  agreeTerms: false,
};

const stateOptions: Record<string, string> = {
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
};

const INCLUDED = [
  {
    text: "Prescription to Fast and Effective GLP-1 (cost of medication is included)",
    icon: SemaglutideBottle,
    iconClass: "nf12-includeIconImg nf12-includeIconBottle",
  },
  {
    text: "1:1 Physician Guidance",
    icon: DoctorLogo,
    iconClass: "nf12-includeIconImg nf12-includeIconDoctor",
  },
  {
    text: "Weight Loss Guarantee",
    icon: semaIconBadge,
    iconClass: "nf12-includeIconImg nf12-includeIconBadge",
  },
];

const isProd = process.env.REACT_APP_ENV === "production";
const STRIPE_KEY = isProd
  ? "pk_live_51PsWA5GejApNyv7Xuy48c50fEyWxIqSglHCR33zJQNzXYUG4DHu4hpbRM80SVkXNrSPgimj1ioe3cVabLPjgN3bh00GkcYUa3H"
  : "pk_test_51PsWA5GejApNyv7Xdo7ye2sTXDMhrfaFsd15JKinSzBWaSR3iqAwTgEkbm9ZCPzit4RpxVotpvaPScTPiwwjG1ht00qiPluAWx";

const stripePromise = loadStripe(STRIPE_KEY);

const BASE_URL =
  process.env.REACT_APP_ENV === "production"
    ? "https://claya-ruby-b715fff96163.herokuapp.com"
    : "https://claya-stage-ruby-03e61b6e8c6b.herokuapp.com";

// default coupon in old funnel style
const DEFAULT_COUPON_CODE = isProd ? "100CLAYA" : "100TestClaya";

function lsGet<T = any>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "value" in parsed)
      return parsed.value as T;
    return parsed as T;
  } catch {
    return (localStorage.getItem(key) as any) ?? null;
  }
}

function normalizeZip(zip: string) {
  return (zip || "").replace(/[^\d]/g, "").slice(0, 5);
}
function normalizePhone(phone: string) {
  return (phone || "").replace(/[^\d]/g, "").slice(0, 10);
}

function getItemIdForGTM(key: string) {
  const map: Record<string, number> = {
    "semaglutide-injectable": 1,
    "tirzepatide-injectable": 2,
    "semaglutide-oral": 3,
    "tirzepatide-oral": 4,
  };
  return map[key] || null;
}

function toApiType(opt: OptionType): ApiMedType {
  return opt === "injections" ? "injectable" : "oral";
}

function productKeyForBackend(med: MedChoice, opt: OptionType) {
  return `${med}-${toApiType(opt)}`;
}

function readMwlModality(): { modality: string; isSyncVisit: boolean } {
  const raw = localStorage.getItem("mwl_modality") || "";
  let modality = raw;

  try {
    const parsed = JSON.parse(raw);
    modality = parsed?.value ?? raw;
  } catch (e) {
    console.log(e);
  }

  const normalized = (modality || "").toLowerCase().trim();

  return {
    modality: normalized,
    isSyncVisit: normalized === "sync_visit",
  };
}

const elementStyle = {
  style: {
    base: {
      fontSize: window.innerWidth > 600 ? "14px" : "16px",
      color: "#32325d",
    },
    invalid: { color: "#fa755a" },
  },
};

function parseMMSS(input: string) {
  const [m, s] = (input || "").split(":");
  const mm = Number(m);
  const ss = Number(s);
  if (!Number.isFinite(mm) || !Number.isFinite(ss)) return 0;
  return Math.max(0, mm * 60 + ss);
}
function formatMMSS(totalSeconds: number) {
  const t = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(t / 60);
  const ss = t % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

type CouponState = { code: string; discountCents: number };

const NewFunnelStep12Inner: React.FC = () => {
  const brandName = "claya";

  const APPROVAL_START = "10:00";
  const [approvalLeftSec, setApprovalLeftSec] = useState(() =>
    parseMMSS(APPROVAL_START)
  );

  const navigate = useNavigate();

  const stripe = useStripe();
  const elements = useElements();

  // selection
  const [selectedMed, setSelectedMed] = useState<MedChoice>("semaglutide");
  const [prefByMed, setPrefByMed] = useState<Record<MedChoice, OptionType>>({
    semaglutide: "injections",
    tirzepatide: "injections",
  });

  const selectedOption = prefByMed[selectedMed];
  const apiKey = productKeyForBackend(selectedMed, selectedOption);

  // form
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);

  // disable shipping fields if already provided
  const [shippingLocked, setShippingLocked] = useState({
    fullName: false,
    email: false,
    address1: false,
    address2: false,
    city: false,
    state: false,
    zip: false,
    phone: false,
  });

  // pricing
  const [pricingLoading, setPricingLoading] = useState(false);
  const [priceId, setPriceId] = useState<string>("");
  const [unitAmountCents, setUnitAmountCents] = useState<number>(0);
  const [stripeProductId, setStripeProductId] = useState<string>("");

  // errors
  const [loading, setLoading] = useState(false);
  const [isPaymentErr, setIsPaymentErr] = useState<string>("");
  const [patientErr, setPatientErr] = useState<string>("");

  const [gender, setGender] = useState<string>("");
  const [goalWeight, setGoalWeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");

  // field errors
  const [fullNameError, setFullNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [address1Error, setAddress1Error] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [zipError, setZipError] = useState("");

  const [billingAddress1Error, setBillingAddress1Error] = useState("");
  const [billingCityError, setBillingCityError] = useState("");
  const [billingStateError, setBillingStateError] = useState("");
  const [billingZipError, setBillingZipError] = useState("");

  // stripe element errors
  const [cardNumberErr, setCardNumberErr] = useState("");
  const [cardExpiryErr, setCardExpiryErr] = useState("");
  const [cardCvcErr, setCardCvcErr] = useState("");

  const [isCardNumberValid, setIsCardNumberValid] = useState(false);
  const [isCardExpiryValid, setIsCardExpiryValid] = useState(false);
  const [isCardCvcValid, setIsCardCvcValid] = useState(false);
  const isCardValid = isCardNumberValid && isCardExpiryValid && isCardCvcValid;

  const chosenRef = useRef<HTMLDivElement | null>(null);
  const checkoutFormRef = useRef<HTMLDivElement | null>(null);

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // localStorage keys
  const LS_KEY_RECOMMENDATION = "Rrecommendation";
  const LS_KEY_RECOMMENDED_TYPE = "recommendedType";
  type RecoKind = "affordability" | "potency" | "";
  type RecoType = "injection" | "tablet" | "";

  const readLS = (key: string) => {
    try {
      const v = localStorage.getItem(key);
      return (v || "").trim();
    } catch {
      return "";
    }
  };

  // ✅ resolve recommendation config
  const reco = useMemo(() => {
    const r = (readLS(LS_KEY_RECOMMENDATION) as RecoKind) || "";
    const t = (readLS(LS_KEY_RECOMMENDED_TYPE) as RecoType) || "";

    const isAff = r === "affordability";
    const isPot = r === "potency";
    const isInj = t === "injection";
    const isTab = t === "tablet";

    const fallback = {
      titleBold: "Semaglutide injections",
      desc: "This is the most affordable and popular option of all GLP-1 medications.",
      bottleImg: SemaglutideOrgBottle,
      miniImg: SemaglutideBottle,
    };

    if (!((isAff || isPot) && (isInj || isTab))) return fallback;

    if (isAff) {
      return {
        titleBold: isInj ? "Semaglutide injections" : "Semaglutide tablets",
        desc: "This is the most affordable and popular option of all GLP-1 medications.",
        bottleImg: isInj ? SemaglutideOrgBottle : OralSemaglutideImg,
        miniImg: isInj ? SemaglutideBottle : OralSemaglutideImg,
      };
    }

    return {
      titleBold: isInj ? "Tirzepatide injections" : "Tirzepatide tablets",
      desc: "This is the most potent GLP-1 medication option.",
      bottleImg: isInj ? TirezepatideBottle : OralTirzepatideImg,
      miniImg: isInj ? TirezepatideBottle : OralTirzepatideImg,
    };
  }, []);

  const inputError = (msg: string) => {
    if (!msg) return null;
    return (
      <div className="checkout-state-error">
        <img src={Alert} alt="error" width={16} height={15} />
        <p className="checkout-state-error-text">{msg}</p>
      </div>
    );
  };

  // ======================= COUPON (OLD FUNNEL STYLE) =======================
  const DEFAULT = DEFAULT_COUPON_CODE;

  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const couponInputRef = useRef<HTMLInputElement | null>(null);

  const selectionKey = `${selectedMed}-${selectedOption}`; // per-card selection key

  const [couponBySelection, setCouponBySelection] = useState<
    Record<string, CouponState | null>
  >({});
  const [skipAutoApplyBySelection, setSkipAutoApplyBySelection] = useState<
    Record<string, boolean>
  >({});

  const activeCoupon = couponBySelection[selectionKey];
  const isCouponApplied = !!activeCoupon;

  const [couponLoading, setCouponLoading] = useState(false);
  const [couponUIFreeze, setCouponUIFreeze] = useState(false);
  const [couponErr, setCouponErr] = useState<string>("");

  const defaultApplied =
    isCouponApplied &&
    (activeCoupon?.code || "").toUpperCase() === DEFAULT.toUpperCase();

  const nonDefaultApplied =
    isCouponApplied &&
    (activeCoupon?.code || "").toUpperCase() !== DEFAULT.toUpperCase();

  const discountCents = activeCoupon?.discountCents ?? 0;
  const discountForDisplay = couponLoading ? 0 : discountCents;

  const setCoupon = (key: string, coupon: CouponState | null) => {
    setCouponBySelection((prev) => ({ ...prev, [key]: coupon }));
  };

  const clearCoupon = (key: string) => {
    setCouponBySelection((prev) => ({ ...prev, [key]: null }));
  };

  const validateCouponForCurrent = async (code: string) => {
    const trimmed = (code || "").trim();
    if (!trimmed)
      return { ok: false, discountCents: 0, msg: "Enter a coupon code" };

    if (!unitAmountCents || !stripeProductId) {
      return { ok: false, discountCents: 0, msg: "Pricing not ready yet" };
    }

    const data = await validateCoupon(
      trimmed,
      unitAmountCents,
      stripeProductId
    );

    const ok =
      data?.valid === true ||
      data?.success === true ||
      data?.is_valid === true ||
      data?.isValid === true;

    if (!ok)
      return {
        ok: false,
        discountCents: 0,
        msg: data?.message || "Invalid coupon code",
      };

    const discountDollars = Number(data?.discount || 0);
    const cents = Number.isFinite(discountDollars)
      ? Math.round(discountDollars * 100)
      : 0;

    return { ok: true, discountCents: cents, msg: "" };
  };

  const applyCoupon = async (code?: string) => {
    const raw = (code ?? couponCode ?? "").trim();
    if (!raw) {
      setCouponErr("Enter a coupon code");
      return;
    }

    setCouponErr("");
    setCouponUIFreeze(true);
    setCouponLoading(true);

    try {
      const res = await validateCouponForCurrent(raw);

      if (!res.ok) {
        clearCoupon(selectionKey);
        setCouponErr(res.msg || "Invalid coupon code");
        return;
      }

      // manual apply / explicit apply allows default auto again later if they remove this
      setSkipAutoApplyBySelection((prev) => ({
        ...prev,
        [selectionKey]: false,
      }));

      setCoupon(selectionKey, { code: raw, discountCents: res.discountCents });
      setCouponCode(raw);
    } catch (e: any) {
      setCouponErr(e?.message || "Invalid coupon");
      clearCoupon(selectionKey);
    } finally {
      setCouponLoading(false);
      setCouponUIFreeze(false);
    }
  };

  const removeCoupon = () => {
    // user intentionally removed => stop auto-apply default for this selection
    setSkipAutoApplyBySelection((prev) => ({ ...prev, [selectionKey]: true }));
    clearCoupon(selectionKey);
    setCouponErr("");
    setCouponCode("");
  };

  const reapplyDefaultCoupon = async () => {
    setSkipAutoApplyBySelection((prev) => ({ ...prev, [selectionKey]: false }));
    await applyCoupon(DEFAULT);
  };

  // optional: load coupon from LS once (same as your old hook)
  useEffect(() => {
    const lsPromo = (
      lsGet<string>("coupon_code") ||
      lsGet<string>("coupon") ||
      lsGet<string>("promo_code") ||
      lsGet<string>("promoCode") ||
      ""
    )
      ?.toString()
      .trim();

    if (lsPromo) setCouponCode(lsPromo);
  }, []);

  // countdown
  useEffect(() => {
    if (approvalLeftSec <= 0) return;
    const id = window.setInterval(() => {
      setApprovalLeftSec((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [approvalLeftSec]);

  // ✅ Autofill once and lock
  useEffect(() => {
    const firstName = (lsGet<string>("firstName") || "").toString();
    const lastName = (lsGet<string>("lastName") || "").toString();
    const email = (lsGet<string>("email") || "").toString();

    const goalWeightValue = (
      lsGet("glp1_intake_step1")?.goalWeight || ""
    ).toString();
    const genderValue = (lsGet("glp1_intake_step1")?.gender || "").toString();
    const weightValue = (lsGet("glp1_intake_step1")?.weight || "").toString();

    const address1 = (
      lsGet<string>("addressLine1") ||
      lsGet<string>("address1") ||
      ""
    ).toString();
    const address2 = (
      lsGet<string>("addressLine2") ||
      lsGet<string>("address2") ||
      ""
    ).toString();
    const city = (lsGet<string>("city") || "").toString();
    const state = (lsGet<string>("state") || "").toString();
    const zip = (lsGet<string>("zip") || "").toString();
    const phone = (lsGet<string>("phone") || "").toString();

    const fullName = `${firstName} ${lastName}`.trim();

    setGoalWeight(goalWeightValue);
    setWeight(weightValue);
    setGender(genderValue);

    setForm((p) => ({
      ...p,
      fullName: fullName || p.fullName,
      email: email || p.email,
      address1: address1 || p.address1,
      address2: address2 || p.address2,
      city: city || p.city,
      state: state || p.state,
      zip: zip || p.zip,
      phone: normalizePhone(phone) || p.phone,
    }));

    setShippingLocked({
      fullName: !!fullName,
      email: !!email,
      address1: !!address1,
      address2: !!address2,
      city: !!city,
      state: !!state,
      zip: !!zip,
      phone: !!phone,
    });
  }, []);

  // ✅ Fetch Stripe prices
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setPricingLoading(true);
      setIsPaymentErr("");

      // reset coupon error (keep couponBySelection, because old funnel keeps per selection)
      setCouponErr("");

      try {
        const resp = await getStripeProducts(apiKey);
        const list: StripeProduct[] = (resp?.data || []) as StripeProduct[];

        const recurring = list
          .filter((p) => p?.active === true && p?.type === "recurring")
          .sort(
            (a, b) =>
              (a?.recurring?.interval_count || 0) -
              (b?.recurring?.interval_count || 0)
          );

        const oneMonth = recurring[0];
        if (!oneMonth) throw new Error("No recurring price found.");

        if (cancelled) return;
        setPriceId(oneMonth.id);
        setUnitAmountCents(Number(oneMonth.unit_amount || 0));
        setStripeProductId((oneMonth as any).product || "");
      } catch (e: any) {
        if (cancelled) return;
        setPriceId("");
        setUnitAmountCents(0);
        setStripeProductId("");
        setIsPaymentErr(e?.message || "Failed to fetch pricing.");
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // ✅ OLD FUNNEL AUTO-APPLY / REVALIDATE
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!unitAmountCents || !stripeProductId) return;

      const current = activeCoupon?.code?.trim();

      // If there is already a coupon stored, revalidate it (product or price changed)
      if (current) {
        setCouponUIFreeze(true);
        setCouponLoading(true);
        setCouponErr("");
        try {
          const res = await validateCouponForCurrent(current);
          if (cancelled) return;

          if (res.ok) {
            setCoupon(selectionKey, {
              code: current,
              discountCents: res.discountCents,
            });
          } else {
            // invalid coupon => clear it
            clearCoupon(selectionKey);
            setCouponErr(res.msg || "Invalid coupon code");

            // try default ONLY if user didn’t remove intentionally
            if (!skipAutoApplyBySelection[selectionKey]) {
              const def = await validateCouponForCurrent(DEFAULT);
              if (cancelled) return;
              if (def.ok) {
                setCoupon(selectionKey, {
                  code: DEFAULT,
                  discountCents: def.discountCents,
                });
                setCouponErr("");
              }
            }
          }
        } catch (e: any) {
          if (cancelled) return;
          setCouponErr(e?.message || "Failed to validate coupon");
        } finally {
          if (!cancelled) {
            setCouponLoading(false);
            setCouponUIFreeze(false);
          }
        }
        return;
      }

      // No coupon applied => auto apply default unless user removed
      if (skipAutoApplyBySelection[selectionKey]) return;

      setCouponUIFreeze(true);
      setCouponLoading(true);
      setCouponErr("");
      try {
        const def = await validateCouponForCurrent(DEFAULT);
        if (cancelled) return;
        if (def.ok)
          setCoupon(selectionKey, {
            code: DEFAULT,
            discountCents: def.discountCents,
          });
      } catch {
        // ignore
      } finally {
        if (!cancelled) {
          setCouponLoading(false);
          setCouponUIFreeze(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectionKey, unitAmountCents, stripeProductId]);

  const MED_DATA = {
    semaglutide: {
      name: "Semaglutide",
      badge: "MOST POPULAR",
      bottle: SemaglutideOrgBottle,
      oralBottle: OralSemaglutideImg,
      reviews: "29.9K REVIEWS",
      basePrice: 179,
      oldPrice: 279,
      infoBoxTone: "tan",
    },
    tirzepatide: {
      name: "Tirzepatide",
      badge: "MOST POTENT",
      bottle: TirezepatideBottle,
      oralBottle: OralTirzepatideImg,
      reviews: "18.6K REVIEWS",
      basePrice: 279,
      oldPrice: 379,
      infoBoxTone: "blue",
    },
  } as const;

  const activeMed = MED_DATA[selectedMed];

  // price display uses coupon discount
  const displayPrice = useMemo(() => {
    const base = unitAmountCents || activeMed.basePrice * 100;
    const final = Math.max(base - discountForDisplay, 0);
    return Math.round(final / 100);
  }, [unitAmountCents, activeMed.basePrice, discountForDisplay]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const validateAll = () => {
    let ok = true;

    const fullName = form.fullName.trim();
    const email = form.email.trim();
    const address1 = form.address1.trim();
    const city = form.city.trim();
    const state = (form.state || "").trim();
    const zip = normalizeZip(form.zip);
    const phone = normalizePhone(form.phone);

    setZipError("");
    setStateError("");
    setCityError("");
    setAddress1Error("");
    setPhoneError("");
    setEmailError("");
    setFullNameError("");

    setBillingAddress1Error("");
    setBillingCityError("");
    setBillingStateError("");
    setBillingZipError("");

    if (!fullName) {
      setFullNameError("Please enter your full name");
      ok = false;
    }
    if (
      !email ||
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
    ) {
      setEmailError("Please enter a valid email");
      ok = false;
    }
    if (!address1) {
      setAddress1Error("Please enter your Address Line 1");
      ok = false;
    }
    if (!city) {
      setCityError("Please enter your city");
      ok = false;
    }
    if (!state) {
      setStateError("Please select your state");
      ok = false;
    }
    if (!zip || zip.length !== 5) {
      setZipError("Please enter a valid zip code");
      ok = false;
    }
    if (form.phone && phone.length !== 10) {
      setPhoneError("Please enter a valid 10-digit phone number");
      ok = false;
    }

    if (form.useDifferentBilling) {
      if (!form.billingAddress1.trim()) {
        setBillingAddress1Error("Please enter your Address Line 1");
        ok = false;
      }
      if (!form.billingCity.trim()) {
        setBillingCityError("Please enter your city");
        ok = false;
      }
      if (!form.billingState.trim()) {
        setBillingStateError("Please select your state");
        ok = false;
      }
      const bz = normalizeZip(form.billingZip);
      if (!bz || bz.length !== 5) {
        setBillingZipError("Please enter a valid zip");
        ok = false;
      }
    }

    if (!form.agreeTerms) ok = false;
    if (!isCardValid) ok = false;
    if (!priceId || pricingLoading) ok = false;

    // block while coupon validating (old funnel freeze)
    if (couponLoading || couponUIFreeze) ok = false;

    return ok;
  };

  const canSubmit = useMemo(() => {
    const zipOk = normalizeZip(form.zip).length === 5;
    const shippingOk =
      form.fullName.trim() &&
      form.email.trim() &&
      form.address1.trim() &&
      form.city.trim() &&
      form.state &&
      zipOk;

    const phoneOk = !form.phone || normalizePhone(form.phone).length === 10;

    const billingOk = !form.useDifferentBilling
      ? true
      : form.billingAddress1.trim() &&
        form.billingCity.trim() &&
        form.billingState.trim() &&
        normalizeZip(form.billingZip).length === 5;

    return Boolean(
      shippingOk &&
        phoneOk &&
        billingOk &&
        form.agreeTerms &&
        isCardValid &&
        priceId &&
        !pricingLoading &&
        !couponLoading &&
        !couponUIFreeze &&
        !loading
    );
  }, [
    form,
    isCardValid,
    priceId,
    pricingLoading,
    couponLoading,
    couponUIFreeze,
    loading,
  ]);

  const pushPurchaseEvent = (transactionID: string, amount: number) => {
    if (localStorage.getItem("GTMPurchaseEventFired")) return;

    const itemId = getItemIdForGTM(apiKey);
    if (!itemId) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        transaction_id: transactionID,
        value: String(amount),
        currency: "USD",
        items: [
          {
            item_id: itemId,
            item_name: apiKey,
            price: String(amount),
            quantity: 1,
            plan_selected: "1 month",
          },
        ],
      },
    });

    localStorage.setItem("GTMPurchaseEventFired", "true");
    localStorage.setItem("GTMPurchaseEventTimestamp", Date.now().toString());
  };

  const fireTrackdeskPurchase = (params: {
    productType: "semaglutide" | "tirzepatide";
    orderId: string;
    email: string;
    amount: string;
  }) => {
    try {
      if (typeof window.trackdesk !== "function") return;

      const key = `td_purchase_${params.orderId}`;
      if (localStorage.getItem(key) === "1") return;

      window.trackdesk("claya-affiliate", "conversion", {
        conversionType: params.productType,
        externalId: params.orderId,
        customParams: { advS1: params.email },
        amount: { value: params.amount },
      });

      localStorage.setItem(key, "1");
    } catch (e) {
      console.log(e);
    }
  };

  const everflowConversion = (amount: number) => {
    const script = document.createElement("script");
    const executed = window.localStorage.getItem("EFconversionCodeExecuted");
    script.src = "https://www.sffpl8trk.com/scripts/sdk/everflow.js";
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => {
      try {
        if (window.EF && executed !== "true") {
          window.EF.conversion({
            offer_id: parseInt(localStorage.getItem("offer_id") || "", 10),
            affiliate_id: localStorage.getItem("affiliate_id") || "",
            amount: amount || undefined,
          });
          window.localStorage.setItem("EFconversionCodeExecuted", "true");
          window.fbq?.("track", "Purchase", { currency: "USD", value: amount });
        }
      } catch (e) {
        console.log(e);
      }
    };
    document.body.appendChild(script);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setIsPaymentErr("");
    setPatientErr("");

    setCardNumberErr("");
    setCardExpiryErr("");
    setCardCvcErr("");

    if (!stripe || !elements) {
      setIsPaymentErr("Stripe is not ready. Please refresh the page.");
      return;
    }

    const ok = validateAll();
    if (!ok) {
      setIsPaymentErr("Please fix the highlighted fields.");
      return;
    }

    setLoading(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        setIsPaymentErr("Card number element not found.");
        setLoading(false);
        return;
      }

      const email = form.email.trim();
      const fullName = form.fullName.trim();

      const uuidValue = (lsGet<any>("uuid") as any) || "";
      const uuid =
        typeof uuidValue === "string"
          ? uuidValue
          : uuidValue?.value || uuidValue?.key || uuidValue?.uuid || "";

      const dob = (
        lsGet("glp1_intake_step1")?.dob ||
        lsGet("dob") ||
        ""
      ).toString();
      const gender = (
        lsGet("glp1_intake_step1")?.gender ||
        lsGet("gender") ||
        ""
      ).toString();
      const height = (
        lsGet("glp1_intake_step1")?.height ||
        lsGet("height") ||
        ""
      ).toString();
      const weight = (
        lsGet("glp1_intake_step1")?.weight ||
        lsGet("weight") ||
        ""
      ).toString();

      // update patient
      try {
        await updateExistingPatient(
          email,
          dob,
          gender,
          height,
          weight,
          form.city,
          form.address1,
          form.address2,
          form.state,
          normalizeZip(form.zip),
          normalizePhone(form.phone) ||
            (lsGet<string>("phone") || "").toString(),
          "GLP1",
          uuid
        );
      } catch (err: any) {
        setPatientErr(
          err?.message || "Something went wrong while updating patient."
        );
        setLoading(false);
        return;
      }

      // create payment method
      const pmRes = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          email,
          name: fullName,
          address: {
            line1: form.useDifferentBilling
              ? form.billingAddress1
              : form.address1,
            line2: form.useDifferentBilling
              ? form.billingAddress2
              : form.address2,
            city: form.useDifferentBilling ? form.billingCity : form.city,
            state: form.useDifferentBilling ? form.billingState : form.state,
            postal_code: form.useDifferentBilling
              ? normalizeZip(form.billingZip)
              : normalizeZip(form.zip),
            country: "US",
          },
        },
      });

      if (pmRes.error || !pmRes.paymentMethod) {
        setIsPaymentErr(
          pmRes.error?.message || "Failed to create payment method."
        );
        setLoading(false);
        return;
      }

      // create_subscription
      const createRes = await fetch(
        `${BASE_URL}/api/v1/patients/payments/create_subscription`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            first_name: fullName.split(" ")[0] || "",
            last_name: fullName.split(" ").slice(1).join(" ") || "",
            payment_method_id: pmRes.paymentMethod.id,
            price_id: priceId,
            product_name: apiKey,
            uuid,
          }),
        }
      );

      const createData = await createRes.json();
      if (!createRes.ok) {
        setIsPaymentErr(createData?.error || "Failed to create subscription.");
        setLoading(false);
        return;
      }

      const { customer_id, client_secret } = createData || {};
      if (!client_secret || !customer_id) {
        setIsPaymentErr(
          "Missing client_secret/customer_id from create_subscription."
        );
        setLoading(false);
        return;
      }

      // confirm setup intent
      const confirmRes = await stripe.confirmCardSetup(client_secret);
      if (confirmRes.error) {
        setIsPaymentErr(confirmRes.error.message || "Authorization failed.");
        setLoading(false);
        return;
      }

      // update_and_capture_subscription (ADD coupon here)
      const planType = "1 month";
      const captureRes = await fetch(
        `${BASE_URL}/api/v1/patients/payments/update_and_capture_subscription`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id,
            base_price_id: priceId,
            product_name: apiKey,
            planType,
            email,
            promo_code: activeCoupon?.code ? activeCoupon.code : "",
            uuid,
          }),
        }
      );

      const captureData = await captureRes.json();
      if (!captureRes.ok) {
        setIsPaymentErr(
          captureData?.error || "Failed to update and capture subscription."
        );
        setLoading(false);
        return;
      }

      const success = captureData?.success ?? true;
      const transactionID =
        captureData?.transaction_id || captureData?.transactionID || "";

      if (!success) {
        setIsPaymentErr("Payment failed. Please try again.");
        setLoading(false);
        return;
      }

      // ✅ Submit intake form AFTER successful payment
      try {
        const stored = localStorage.getItem("intake_submission_payload");
        if (stored) {
          const parsed = JSON.parse(stored);
          const payload = parsed?.value;

          if (payload) {
            await submitIntakeForm(
              payload,
              payload.email,
              payload.product_category,
              payload.uuid
            );
          }
        }
      } catch (err) {
        console.error("Final intake submission failed:", err);
      }

      // amount for events uses discounted cents
      const baseCents = unitAmountCents || displayPrice * 100;
      const amountCents = Math.max(
        baseCents - (activeCoupon?.discountCents ?? 0),
        0
      );
      const amount = Math.round(amountCents / 100);

      if (transactionID) pushPurchaseEvent(String(transactionID), amount);
      everflowConversion(amount);

      const productType: "semaglutide" | "tirzepatide" =
        selectedMed === "tirzepatide" ? "tirzepatide" : "semaglutide";

      fireTrackdeskPurchase({
        productType,
        orderId: String(transactionID || customer_id),
        email,
        amount: String(amount),
      });

      localStorage.setItem(
        "success_transaction",
        JSON.stringify({ key: "success_transaction", value: true })
      );
      localStorage.setItem(
        "uuidConversion",
        JSON.stringify({ key: "uuidConversion", value: "yes" })
      );
      localStorage.setItem(
        "AmountToPay",
        JSON.stringify({ key: "AmountToPay", value: amount })
      );
      localStorage.setItem("customer_id", String(customer_id));

      const excludedRaw = localStorage.getItem("intake_excluded");
      let isExcluded = false;

      try {
        isExcluded = JSON.parse(excludedRaw || "{}")?.value === true;
      } catch (e) {
        console.log(e);
      }

      const { isSyncVisit } = readMwlModality();

      if (isExcluded) {
        navigate("/glp1/intake-form/disqualified", { replace: true });
      } else if (isSyncVisit) {
        navigate("/glp1/intake-form/step-12", { replace: true });
        // (your booking step if different, adjust path)
      } else {
        navigate("/glp1/intake-form/thank-you", { replace: true });
      }
    } catch (err: any) {
      setIsPaymentErr(err?.message || "Payment processing error.");
    } finally {
      setLoading(false);
    }
  };

  const STEP1_KEY = "glp1_intake_step1";

  const chart = useMemo(() => {
    let goalWeight = (lsGet("glp1_intake_step1")?.goalWeight || "").toString();
    let weight = (lsGet("glp1_intake_step1")?.weight || "").toString();

    try {
      const raw = localStorage.getItem(STEP1_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const w = parsed?.weight?.value ?? parsed?.weight;
        const gw = parsed?.goalWeight?.value ?? parsed?.goalWeight;

        const wn = Number(w);
        const gwn = Number(gw);
        if (Number.isFinite(wn)) weight = wn as any;
        if (Number.isFinite(gwn)) goalWeight = gwn as any;
      }
    } catch (e) {
      console.log(e);
    }

    const clamp = (n: number, min: number, max: number) =>
      Math.max(min, Math.min(max, n));

    const VB_W = 283;
    const VB_H = 132;

    const X_TODAY = 26;
    const X_8W = 141;
    const X_4M = 258;

    const top = 14;
    const bottom = 118;

    const minW = Math.min(Number(weight || 0), Number(goalWeight || 0));
    const maxW = Math.max(Number(weight || 0), Number(goalWeight || 0));
    const pad = Math.max(15, Math.round((maxW - minW) * 0.4));
    const domainMin = Math.max(0, minW - pad);
    const domainMax = maxW + pad;

    const yFromWeight = (w: number) => {
      const t = (w - domainMin) / (domainMax - domainMin || 1);
      const y = bottom - t * (bottom - top);
      return clamp(y, top, bottom);
    };

    const yStart = yFromWeight(Number(weight || 0));
    const yEnd = yFromWeight(Number(goalWeight || 0));
    const yMid = yStart + (yEnd - yStart) * 0.55;

    const x0 = X_TODAY;
    const x1 = X_8W;
    const x2 = X_4M;

    const c1x = x0 + 35;
    const c1y = yStart + 28;

    const c2x = x1 - 30;
    const c2y = yMid - 8;

    const c3x = x1 + 35;
    const c3y = yMid + 14;

    const c4x = x2 - 25;
    const c4y = yEnd;

    const curveD = `M ${x0} ${yStart}
    C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x1} ${yMid}
    C ${c3x} ${c3y}, ${c4x} ${c4y}, ${x2} ${yEnd}`;

    const areaD = `${curveD} L ${VB_W} ${VB_H} L 0 ${VB_H} Z`;

    return { weight, goalWeight, curveD, areaD, X_TODAY, X_8W, X_4M };
  }, []);

  const setMedAndType = (medKey: MedChoice, type: OptionType) => {
    setSelectedMed(medKey);
    setPrefByMed((p) => ({ ...p, [medKey]: type }));

    requestAnimationFrame(() => {
      chosenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const getCardShownPrice = (medKey: MedChoice) => {
    const data = MED_DATA[medKey];
    const base = data.oldPrice; // IMPORTANT: use oldPrice as base in card
    const final = Math.max(base - Math.round(discountForDisplay / 100), 0);
    return final;
  };

  const chosenBottleImg =
    selectedOption === "tablets" ? activeMed.oralBottle : activeMed.bottle;

  return (
    <FunnelLayout>
      <div className="nf12-page">
        <div className="nf12-topHeader">
          <div className="nf12-topHeader__bar">
            {form.fullName} APPROVAL VALID FOR {formatMMSS(approvalLeftSec)}
          </div>
        </div>

        <div className="nf12-container">
          {/* HERO */}
          <section className="nf12-card nf12-heroCard">
            <div className="nf12-heroTitle">
              <div className="nf12-heroTitle-content">
                <div className="nf12-h1">
                  {form.fullName}&apos;s GLP-1 <br />
                  prescription plan
                </div>
                <div className="nf12-h1Accent">approval!</div>
              </div>
              <div className="nf12-starsRow">
                <span className="nf12-stars" aria-hidden="true">
                  ★★★★★
                </span>
                <span className="nf12-starsText">7.8K REVIEWS</span>
              </div>
            </div>

            <div className="nf12-pillRow">
              <div className="nf12-pill">
                <div className="nf12-pill-box">
                  <p className="nf12-pill-box-title">Goal</p>
                  <span className="nf12-pill-box-title-value">
                    {goalWeight}
                  </span>
                </div>
              </div>
              <div className="nf12-pill">
                <div className="nf12-pill-box">
                  <p className="nf12-pill-box-title">Metabolism</p>
                  <span className="nf12-pill-box-title-value">Fat Protein</span>
                </div>
              </div>
              <div className="nf12-pill">
                <div className="nf12-pill-box">
                  <p className="nf12-pill-box-title">Sex</p>
                  <span className="nf12-pill-box-title-value">{gender}</span>
                </div>
              </div>
            </div>

            <p className="nf12-heroPara">
              You get personalized care for your unique biology with{" "}
              <b>prescribed medication</b>, 1:1 physician guidance, and 24/7{" "}
              {brandName} support.
            </p>

            <div className="nf12-heroEmphasis">
              Your medication price never goes up - even when your dose
              increases!
            </div>

            <div className="nf12-chart">
              <div className="nf12-chart__labels">
                <div className="nf12-chip">
                  <div className="nf12-chip__num">{chart.weight}</div>
                  <div className="nf12-chip__unit">LBS</div>
                </div>

                <div className="nf12-chip nf12-chip--right">
                  <div className="nf12-chip__num">{chart.goalWeight}</div>
                  <div className="nf12-chip__unit">LBS</div>
                  <div className="nf12-chip__sub">Goal Weight</div>
                </div>
              </div>
              <div className="nf12-chart__svgWrap">
                <svg
                  className="nf12-chart__svg"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 283 132"
                  preserveAspectRatio="none"
                >
                  {/* horizontal lines */}
                  <line x1="0" y1="24" x2="283" y2="24" className="nf12-grid" />
                  <line x1="0" y1="56" x2="283" y2="56" className="nf12-grid" />
                  <line x1="0" y1="88" x2="283" y2="88" className="nf12-grid" />

                  {/* dashed vertical markers */}
                  <line
                    x1={chart.X_TODAY}
                    y1="14"
                    x2={chart.X_TODAY}
                    y2="118"
                    className="nf12-vdash"
                  />
                  <line
                    x1={chart.X_8W}
                    y1="14"
                    x2={chart.X_8W}
                    y2="118"
                    className="nf12-vdash"
                  />
                  <line
                    x1={chart.X_4M}
                    y1="14"
                    x2={chart.X_4M}
                    y2="118"
                    className="nf12-vdash"
                  />

                  {/* gradient fill area */}
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d={chart.areaD}
                    fill="url(#paint0_linear_19_3548)"
                  />

                  {/* curve line */}
                  <path d={chart.curveD} className="nf12-curve" />

                  <defs>
                    <linearGradient
                      id="paint0_linear_19_3548"
                      x1="88.7543"
                      y1="54.6325"
                      x2="86.7338"
                      y2="129.872"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#96C6DA" stopOpacity="0.4" />
                      <stop offset="1" stopColor="#0570DE" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>
                </svg>

                <div className="nf12-chart__axis">
                  <span>Today</span>
                  <span>8 Weeks</span>
                  <span className="nf12-chart__axis-highlight">4 Months</span>
                </div>
              </div>
            </div>
          </section>

          {/* Recommendation */}
          {/* Recommendation */}
          <section className="nf12-card" style={{ marginBottom: "48px" }}>
            <div className="nf12-recoRow">
              <div>
                <h2 className="nf12-sectionTitle">Our Recommendation</h2>
                <div className="nf12-recoText">
                  <p>
                    Based on your intake form, we recommend{" "}
                    <b>{reco.titleBold}</b>. {reco.desc}
                  </p>
                  <p className="nf12-note">
                    Note: you can choose whichever medication you prefer,
                    regardless of our recommendation!
                  </p>
                </div>
              </div>

              <div className="nf12-recoImg">
                <img
                  className="nf12-bottle"
                  src={reco.bottleImg}
                  alt="Product"
                />
              </div>
            </div>

            <div className="nf12-scoreCard">
              <div className="nf12-scoreLeft">
                <img
                  className="nf12-miniBottle"
                  src={reco.miniImg}
                  alt="Product"
                />
                <div>
                  <div className="nf12-scoreTitle">
                    You have a <span>very high</span> chance of success with
                    Claya prescribed GLP-1 medication
                  </div>
                </div>
              </div>

              <div className="nf12-scoreRight">
                <div className="nf12-scoreBig">94.6%</div>
                <div className="nf12-scoreSmall">VERY HIGH</div>
              </div>
            </div>
          </section>

          <div className="nf12-card-divider"></div>

          {/* Goals / plan / included */}
          <section className="nf12-card" style={{ marginTop: "48px" }}>
            <h2 className="nf12-sectionTitle-goal">
              The goals{" "}
              <span className="nf12-emphasisUnderline">
                you will accomplish
              </span>{" "}
              with your plan
            </h2>

            <div className="nf12-section-goal-box">
              <div className="nf12-section-goal-trophy">🏆</div>
              <ul className="nf12-bullets">
                <li>Lose {Number(weight) - Number(goalWeight)}lbs</li>
                <li>
                  Reset your metabolic “set point” so your body naturally wants
                  to be at {goalWeight} lbs
                </li>
                <li>Look and feel healthier</li>
              </ul>
            </div>

            <div className="nf12-bullets-arrow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="109"
                height="134"
                viewBox="0 0 109 134"
                fill="none"
              >
                <g clipPath="url(#clip0_19_3616)">
                  <path
                    d="M29.2302 132.188C29.2132 133.016 29.8708 133.701 30.6991 133.718L44.1962 133.996C45.0245 134.013 45.7097 133.355 45.7267 132.527C45.7438 131.699 45.0861 131.013 44.2579 130.996L32.2604 130.75L32.5069 118.752C32.5239 117.924 31.8663 117.239 31.038 117.222C30.2098 117.205 29.5246 117.863 29.5076 118.691L29.2302 132.188ZM102.447 1.21871C101.024 1.69509 101.024 1.69484 101.024 1.69476C101.024 1.69441 101.024 1.69493 101.024 1.69476C101.025 1.69545 101.025 1.69733 101.026 1.69924C101.027 1.70305 101.029 1.70927 101.032 1.71788C101.038 1.7351 101.046 1.76187 101.058 1.79794C101.081 1.87009 101.117 1.97945 101.162 2.12413C101.253 2.4135 101.385 2.84407 101.547 3.40068C101.871 4.51412 102.315 6.13066 102.789 8.12942C103.738 12.1308 104.801 17.6447 105.271 23.7094C106.219 35.9469 104.712 49.9326 95.5951 58.5544L97.6564 60.7341C107.821 51.1212 109.224 35.8941 108.262 23.4776C107.777 17.2153 106.681 11.5418 105.708 7.43747C105.221 5.38342 104.764 3.71759 104.428 2.56187C104.259 1.9839 104.121 1.53318 104.024 1.22492C103.976 1.07078 103.938 0.952235 103.911 0.871178C103.898 0.830648 103.888 0.799488 103.881 0.777936C103.877 0.76716 103.874 0.758785 103.873 0.752841C103.872 0.749869 103.871 0.747505 103.87 0.745753C103.87 0.744877 103.87 0.744021 103.869 0.743583C103.869 0.742882 103.869 0.742333 102.447 1.21871ZM95.5951 58.5544C90.9489 62.9483 83.4515 67.5141 74.6016 71.3763C65.771 75.2301 55.6963 78.3388 45.9611 79.8734C36.203 81.4117 26.9129 81.3485 19.5804 78.982C12.3164 76.6377 7.02292 72.064 4.93784 64.4704L2.04491 65.2648C4.44882 74.0194 10.6315 79.2463 18.659 81.8371C26.6181 84.4057 36.4352 84.4121 46.4282 82.8368C56.4442 81.2579 66.7646 78.0698 75.8015 74.1259C84.8192 70.1904 92.6617 65.4576 97.6564 60.7341L95.5951 58.5544ZM4.93784 64.4704C2.75923 56.5362 3.0644 49.9981 4.91444 44.8308C6.76328 39.6669 10.1901 35.771 14.4345 33.1637C22.975 27.9174 34.7876 27.9172 43.4305 33.4353L45.0449 30.9067C35.4252 24.765 22.3757 24.7647 12.8642 30.6075C8.0825 33.5448 4.18588 37.9657 2.09001 43.8196C-0.00467086 49.6701 -0.265469 56.8507 2.04491 65.2648L4.93784 64.4704ZM43.4305 33.4353C51.9024 38.8443 55.6899 50.3976 55.7709 63.8808C55.8513 77.2804 52.2512 92.091 46.5384 103.338L49.2131 104.696C55.1421 93.024 58.8542 77.7415 58.7708 63.8628C58.688 50.0678 54.8355 37.1577 45.0449 30.9067L43.4305 33.4353ZM46.5384 103.338C40.7385 114.756 36.4831 121.75 33.6947 125.872C32.3009 127.933 31.2761 129.272 30.6107 130.086C30.2781 130.492 30.0359 130.767 29.8828 130.934C29.8063 131.018 29.7522 131.074 29.7203 131.107C29.7043 131.123 29.6939 131.134 29.6891 131.139C29.6867 131.141 29.6857 131.142 29.686 131.142C29.6862 131.141 29.6867 131.141 29.6876 131.14C29.688 131.14 29.6885 131.139 29.6891 131.139C29.6894 131.138 29.69 131.138 29.6901 131.138C29.6907 131.137 29.6913 131.137 30.7299 132.219C31.7686 133.301 31.7692 133.3 31.7698 133.3C31.77 133.3 31.7707 133.299 31.7711 133.298C31.7721 133.298 31.7731 133.297 31.7742 133.296C31.7764 133.293 31.779 133.291 31.7819 133.288C31.7876 133.282 31.7946 133.276 31.803 133.267C31.8197 133.251 31.8416 133.229 31.8688 133.201C31.9232 133.145 31.9987 133.066 32.0955 132.96C32.2889 132.749 32.5676 132.432 32.9329 131.985C33.6634 131.092 34.7421 129.678 36.1796 127.553C39.0537 123.304 43.3711 116.198 49.2131 104.696L46.5384 103.338Z"
                    fill="#442E74"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_19_3616">
                    <rect width="109" height="134" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>

            <div className="nf12-planRow">
              <div className="nf12-planLeft">
                <h3 className="nf12-subTitle-plan">Your plan</h3>
                <p className="nf12-muted">
                  You will get <b>everything you need</b> to drop{" "}
                  {Number(weight) - Number(goalWeight)} lbs and keep it off.
                </p>
              </div>

              <div className="nf12-planRight">
                <img src={ClayaPhonePencil} alt="plan" />
              </div>
            </div>

            <div className="nf12-included">
              <h3 className="nf12-subTitle-included nf12-center">
                What is included?
              </h3>

              <div className="nf12-includedList">
                {INCLUDED.map((item) => (
                  <div className="nf12-includeRow" key={item.text}>
                    <img
                      className={item.iconClass}
                      src={item.icon}
                      alt=""
                      aria-hidden="true"
                    />
                    <div className="nf12-includeText">{item.text}</div>
                  </div>
                ))}
              </div>

              <div className="nf12-includeFooterText nf12-center">
                + 24/7 support and free shipping!
              </div>
            </div>
          </section>
          {/* What happens next */}
          <section className="nf12-card">
            <div className="nf12-card-happens-next">
              <div className="nf12-steps">
                <h2 className="nf12-subTitle-included nf12-center">
                  What Happens Next?
                </h2>

                {[
                  {
                    step: "STEP 1",
                    title: "Physician Review",
                    body: "You’re already pre-qualified. After checkout, a board-certified physician will review your information and begin the approval process.",
                  },
                  {
                    step: "STEP 2",
                    title: "Fast Prescription Approval",
                    body: "Most prescriptions are approved in less than 24 hours. If needed, same-day consultations with a licensed clinician are available — at no extra charge.",
                  },
                  {
                    step: "STEP 3",
                    title: "Medication Shipping",
                    body: "Once approved, your medication is prepared and shipped. You’ll receive tracking info within 2 business days, and your prescription will be on its way.",
                  },
                  {
                    step: "STEP 4",
                    title: "Monthly Refills",
                    body: "At the end of each month, you’ll fill out a quick refill form. We’ll send reminders and tracking info as your next shipment heads your way.",
                  },
                  {
                    step: "STEP 5",
                    title: "Unlimited Support",
                    body: "Have questions about your progress, side effects, or dosage? You’ll have unlimited, 24/7 access to our care team and licensed clinicians — whenever you need us.",
                  },
                ].map((s) => (
                  <div className="nf12-step" key={s.step}>
                    <div className="nf12-step__kicker">{s.step}</div>
                    <div className="nf12-step__title">{s.title}</div>
                    <div className="nf12-step__body">{s.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="nf12-card">
            <div className="nf12-card-happens-next">
              <div className="nf12-saveSmall">
                Your approval is reserved for{" "}
                <span>{formatMMSS(approvalLeftSec)}</span>
              </div>
              <div className="nf12-saveBig">
                Save Over $100 <span className="nf12-italic">Instantly</span>
              </div>
              <div className="nf12-saveTiny">
                Pay one month at a time.
                <br /> No contracts, cancel anytime without <br />
                speaking to anyone.
              </div>
              <div className="nf12-saveTiny-text">
                24/7 Support + unlimited doctor visits + <br /> medication{" "}
                <span className="nf12-italic-text">all included.</span>
              </div>
              <div className="nf12-saveHead-footer">
                Choose your medication preference <br />
                below:
              </div>
            </div>
          </section>

          {/* ✅✅ TWO MEDICATION CARDS (always show) */}
          <div className="nf12-card">
            {(["semaglutide", "tirzepatide"] as const).map((mKey) => {
              const data = MED_DATA[mKey];
              const isSelected = selectedMed === mKey;
              const type = prefByMed[mKey];
              const cardPrice = getCardShownPrice(mKey);

              const tabletsLabel =
                mKey === "semaglutide" ? `TABLETS` : "TABLETS";

              return (
                <div
                  key={mKey}
                  className={
                    "nf12-productCard nf12-productCard--stacked" +
                    (isSelected ? "nf12-productCard--selected" : "")
                  }
                >
                  <div className="nf12-medBadge-box">
                    {" "}
                    <div className="nf12-medBadge">{data.badge}</div>
                    <div className="nf12-medTitle">{data.name}</div>
                  </div>

                  <div className="nf12-productHero">
                    <img
                      className="nf12-productBottleImg"
                      src={data.bottle}
                      alt={`${data.name} bottle`}
                    />
                  </div>

                  <div className="nf12-productStarsRow">
                    <span className="nf12-productStars" aria-hidden="true">
                      ★★★★★
                    </span>
                    <span className="nf12-productReviews">{data.reviews}</span>
                  </div>

                  <div className="nf12-productPriceLine">
                    {isCouponApplied ? (
                      <>
                        <div className="nf12-productPriceText">
                          Prescribed for only{" "}
                          <span className="nf12-green">${cardPrice}</span>
                        </div>

                        <div className="nf12-productOld nf12-lineThrough">
                          ${data.oldPrice}
                        </div>
                      </>
                    ) : (
                      <div className="nf12-productPriceText">
                        Prescribed for only{" "}
                        <span className="nf12-regularPrice" style={{ color: "#79a75d"}}> 
                          ${data.oldPrice}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="nf12-productChecks">
                    {[
                      "Available in both oral and easy injectable forms",
                      "Save $100 instantly on your first month",
                      "PRICE INCLUDES: doctor consult, unlimited 1:1 medical support, written prescription + 4 weeks of medicine and free shipping",
                    ].map((t) => (
                      <div className="nf12-checkRow" key={t}>
                        <span className="nf12-checkIcon" aria-hidden="true">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M3.75 10.625L8.75 15.625L16.25 4.375"
                              stroke="#0C3972"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>

                  <div className="nf12-productPref">
                    <div className="nf12-productPrefTitle">
                      Which do you prefer?
                    </div>

                    <button
                      type="button"
                      className={
                        "nf12-prefBtn " +
                        (type === "injections" ? "nf12-prefBtn--active" : "")
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setMedAndType(mKey, "injections");
                      }}
                    >
                      INJECTIONS
                    </button>

                    <button
                      type="button"
                      className={
                        "nf12-prefBtn " +
                        (type === "tablets" ? "nf12-prefBtn--active" : "")
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setMedAndType(mKey, "tablets");
                      }}
                    >
                      {tabletsLabel}
                    </button>
                  </div>

                  <div
                    className={
                      "nf12-productInfoBox nf12-productInfoBox--greenChecks " +
                      (data.infoBoxTone === "tan"
                        ? "nf12-productInfoBox--tan"
                        : "nf12-productInfoBox--blue")
                    }
                  >
                    {[
                      mKey === "semaglutide"
                        ? "Most popular, most affordable GLP-1 weight loss medication"
                        : "Most potent GLP-1 weight loss medication",
                      "Prescribed and shipped free within 2 days",
                      "Prescribed by world-class, US-based, board-certified clinicians",
                      "Send a message to your doctor 24/7",
                    ].map((t) => (
                      <div className="nf12-infoRow" key={t}>
                        <span className="nf12-infoCheck" aria-hidden="true">
                          ✅
                        </span>
                        <span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Currently chosen (match screenshot) */}
          <section className="nf12-card" ref={chosenRef}>
            <div className="nf12-chosen-box">
              <div className="nf12-chosenTitle">
                Currently Chosen:{" "}
                <span className="nf12-underlineSoft">{activeMed.name}</span>{" "}
                {selectedOption === "injections" ? "Injection" : "Tablets"} for
                ${displayPrice}
              </div>

              <div className="nf12-chosenBottleWrap">
                <img
                  className="nf12-chosenBottleImg"
                  src={chosenBottleImg}
                  alt={`${activeMed.name} ${
                    selectedOption === "tablets" ? "tablets" : "injection"
                  } bottle`}
                />
              </div>

              <div className="nf12-chosenMidText">
                Everything you need <br /> + free shipping
              </div>

              <div className="nf12-chosenBox">
                {[
                  "One month of medication, supplies and instructions",
                  "Cancel anytime, come back anytime",
                  "24/7 medical support",
                  "You’re saving $100 on your first month",
                  "One-on-one access to our team of physicians",
                  "Our promise to help you as much (or as little) as you want along the way",
                ].map((t) => (
                  <div className="nf12-chosenRow" key={t}>
                    <span className="nf12-chosenCheck" aria-hidden="true">
                      ✅
                    </span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>

              {/* ================= COUPON SECTION (OLD FUNNEL BEHAVIOR) ================= */}
              <div
                className="coupon-container-toggle"
                style={{
                  paddingTop: "24px",
                  paddingBottom: "20px",
                }}
              >
                <div
                  className="coupon-toggle-header coupon-toggle-header-box"
                  onClick={() => setShowCouponInput((p) => !p)}
                >
                  <span className="coupon-box-text" style={{fontSize: "18px"}}>
                    Do you have a coupon code?
                  </span>
                  <span
                    className={`coupon-arrow-icons ${
                      showCouponInput ? "down-arrow" : "up-arrow"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="8"
                      viewBox="0 0 13 8"
                      fill="none"
                    >
                      <path
                        d="M0.84 6.41L6.5 0.75L12.16 6.41L11.45 7.11L6.5 2.16L1.55 7.11L0.84 6.41Z"
                        fill="black"
                      />
                    </svg>
                  </span>
                </div>

                {showCouponInput && (
                  <>
                    {/* Default applied banner */}
                    {defaultApplied && (
                      <div className="coupon-container coupon-container-box">
                        <div className="coupon-container-text">
                          <p>
                            <span>{DEFAULT}</span> Applied. Congrats! You get $
                            {Math.round(
                              (activeCoupon?.discountCents ?? 0) / 100
                            )}{" "}
                            off
                          </p>
                        </div>
                        <button
                          className="coupon-remove-btn"
                          type="button"
                          onClick={removeCoupon}
                        >
                          &times;
                        </button>
                      </div>
                    )}

                    {/* Input + apply (only when default not applied) */}
                    {!defaultApplied && (
                      <>
                        <div className="coupon-input-wrapper">
                          <input
                            type="text"
                            ref={couponInputRef}
                            placeholder="Enter your coupon code"
                            className="coupon-input-with-btn"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            disabled={couponLoading || couponUIFreeze}
                          />

                          <button
                            type="button"
                            className="apply-btn-inside-input"
                            disabled={couponLoading || couponUIFreeze}
                            onClick={() => applyCoupon()}
                            aria-busy={couponLoading || couponUIFreeze}
                          >
                            {couponLoading
                              ? "Applying..."
                              : nonDefaultApplied
                              ? "APPLIED"
                              : "Apply"}
                          </button>
                        </div>

                        {/* Non-default applied chip */}
                        {nonDefaultApplied && (
                          <div className="applied-coupon">
                            <span style={{ fontSize: 12 }}>Coupon Applied</span>
                            <button
                              type="button"
                              onClick={removeCoupon}
                              className="remove-coupon-btn"
                            >
                              X
                            </button>
                          </div>
                        )}

                        {/* Error */}
                        {couponErr && (
                          <p
                            className="error-message"
                            style={{ textAlign: "left" }}
                          >
                            {couponErr}
                          </p>
                        )}
                      </>
                    )}

                    {/* Default tile (always visible when default not applied) */}
                    {!defaultApplied && (
                      <div
                        className="coupon-container coupon-container-box"
                        style={{ cursor: "pointer" }}
                        onClick={reapplyDefaultCoupon}
                      >
                        <div className="coupon-container-text">
                          <p>
                            Save <span>$100</span> with <span>{DEFAULT}</span>
                          </p>
                        </div>
                        <div className="coupon-apply couple-apply-box">
                          {couponLoading ? "..." : "Apply"}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* ================= END COUPON SECTION ================= */}
            </div>
          </section>

          <div className="nf12-hfsafsa-badge">
            <img src={Clayahfsafsa} alt="hfsafsa" />
          </div>
          {/* ✅ Checkout form */}
          <section className="nf12-card" ref={checkoutFormRef}>
            <div className="nf12-card-happens-next nf12-card-form">
              <form onSubmit={onSubmit}>
                <div className="nf12-hipaa">
                  <span className="nf12-hipaaIcon" aria-hidden="true">
                    <img src={SafetyIcon} alt="claya-safety-icon" />
                  </span>
                  <span>
                    Your data is protected by HIPAA. All transactions are
                    secured and encrypted.
                  </span>
                </div>

                <h3 className="nf12-formTitle">Shipping Information</h3>

                <div className="nf12-field">
                  <label className="nf12-label">Full name</label>
                  <input
                    className="nf12-input"
                    value={form.fullName}
                    disabled={shippingLocked.fullName}
                    style={{
                      backgroundColor: shippingLocked.fullName
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => {
                      setField("fullName", e.target.value);
                      if (fullNameError) setFullNameError("");
                    }}
                  />
                  {inputError(fullNameError)}
                </div>

                {/* <div className="nf12-field">
                  <label className="nf12-label">Email</label>
                  <input
                    className="nf12-input"
                    value={form.email}
                    disabled={shippingLocked.email}
                    style={{
                      backgroundColor: shippingLocked.email
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => {
                      setField("email", e.target.value);
                      if (emailError) setEmailError("");
                    }}
                  />
                  {inputError(emailError)}
                </div> */}

                <div className="nf12-field">
                  <label className="nf12-label">Address line 1</label>
                  <input
                    className="nf12-input"
                    value={form.address1}
                    disabled={shippingLocked.address1}
                    style={{
                      backgroundColor: shippingLocked.address1
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => {
                      setField("address1", e.target.value);
                      if (address1Error) setAddress1Error("");
                    }}
                  />
                  {inputError(address1Error)}
                </div>

                <div className="nf12-field">
                  <label className="nf12-label">Address line 2</label>
                  <input
                    className="nf12-input"
                    value={form.address2}
                    disabled={shippingLocked.address2}
                    style={{
                      backgroundColor: shippingLocked.address2
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => setField("address2", e.target.value)}
                    placeholder="Apt., suite, unit number, etc. (optional)"
                  />
                </div>

                <div className="nf12-field">
                  <label className="nf12-label">City</label>
                  <input
                    className="nf12-input"
                    value={form.city}
                    disabled={shippingLocked.city}
                    style={{
                      backgroundColor: shippingLocked.city
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => {
                      setField("city", e.target.value);
                      if (cityError) setCityError("");
                    }}
                  />
                  {inputError(cityError)}
                </div>

                <div className="nf12-grid2">
                  <div className="nf12-field">
                    <label className="nf12-label">State</label>
                    <select
                      className="nf12-input"
                      value={form.state}
                      disabled={shippingLocked.state}
                      style={{
                        backgroundColor: shippingLocked.state
                          ? "rgb(229, 237, 246)"
                          : "",
                      }}
                      onChange={(e) => {
                        setField("state", e.target.value);
                        if (stateError) setStateError("");
                        localStorage.setItem(
                          "state",
                          JSON.stringify({ value: e.target.value })
                        );
                        localStorage.setItem(
                          "fullState",
                          JSON.stringify({
                            key: "fullState",
                            value: stateOptions[e.target.value],
                          })
                        );
                      }}
                    >
                      <option value="">Select a state</option>
                      {Object.entries(stateOptions).map(([abbr, full]) => (
                        <option key={abbr} value={abbr}>
                          {full}
                        </option>
                      ))}
                    </select>
                    {inputError(stateError)}
                  </div>

                  <div className="nf12-field">
                    <label className="nf12-label">ZIP code</label>
                    <input
                      className="nf12-input"
                      value={form.zip}
                      disabled={shippingLocked.zip}
                      style={{
                        backgroundColor: shippingLocked.zip
                          ? "rgb(229, 237, 246)"
                          : "",
                      }}
                      onChange={(e) => {
                        setField("zip", normalizeZip(e.target.value));
                        if (zipError) setZipError("");
                      }}
                      maxLength={5}
                    />
                    {inputError(zipError)}
                  </div>
                </div>

                {/* <div className="nf12-field">
                  <label className="nf12-label">Phone number (optional)</label>
                  <input
                    className="nf12-input"
                    value={form.phone}
                    disabled={shippingLocked.phone}
                    style={{
                      backgroundColor: shippingLocked.phone
                        ? "rgb(229, 237, 246)"
                        : "",
                    }}
                    onChange={(e) => {
                      setField("phone", normalizePhone(e.target.value));
                      if (phoneError) setPhoneError("");
                    }}
                    placeholder="10-digit phone"
                  />
                  {inputError(phoneError)}
                </div> */}

                {/* Billing */}
                <h3 className="nf12-formTitle">Billing Address</h3>
                <label className="nf12-checkBoxRow">
                  <input
                    type="checkbox"
                    checked={form.useDifferentBilling}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setField("useDifferentBilling", checked);

                      // old-like behavior: if turning ON, prefill from shipping if billing empty
                      if (checked) {
                        setForm((p) => ({
                          ...p,
                          billingAddress1: p.billingAddress1 || p.address1,
                          billingAddress2: p.billingAddress2 || p.address2,
                          billingCity: p.billingCity || p.city,
                          billingState: p.billingState || p.state,
                          billingZip: p.billingZip || p.zip,
                        }));
                      }
                    }}
                  />
                  <span>Use a different billing address</span>
                </label>

                {form.useDifferentBilling ? (
                  <>
                    <div className="nf12-field">
                      <label className="nf12-label">Address line 1</label>
                      <input
                        className="nf12-input"
                        value={form.billingAddress1}
                        onChange={(e) => {
                          setField("billingAddress1", e.target.value);
                          if (billingAddress1Error) setBillingAddress1Error("");
                        }}
                      />
                      {inputError(billingAddress1Error)}
                    </div>

                    <div className="nf12-field">
                      <label className="nf12-label">Address line 2</label>
                      <input
                        className="nf12-input"
                        value={form.billingAddress2}
                        onChange={(e) =>
                          setField("billingAddress2", e.target.value)
                        }
                      />
                    </div>

                    <div className="nf12-field">
                      <label className="nf12-label">City</label>
                      <input
                        className="nf12-input"
                        value={form.billingCity}
                        onChange={(e) => {
                          setField("billingCity", e.target.value);
                          if (billingCityError) setBillingCityError("");
                        }}
                      />
                      {inputError(billingCityError)}
                    </div>

                    {/* ✅ Billing state dropdown */}
                    <div className="nf12-grid2">
                      <div className="nf12-field">
                        <label className="nf12-label">State</label>
                        <select
                          className="nf12-input"
                          value={form.billingState}
                          onChange={(e) => {
                            setField("billingState", e.target.value);
                            if (billingStateError) setBillingStateError("");
                          }}
                        >
                          <option value="">Select a state</option>
                          {Object.entries(stateOptions).map(([abbr, full]) => (
                            <option key={abbr} value={abbr}>
                              {full}
                            </option>
                          ))}
                        </select>
                        {inputError(billingStateError)}
                      </div>

                      <div className="nf12-field">
                        <label className="nf12-label">ZIP</label>
                        <input
                          className="nf12-input"
                          value={form.billingZip}
                          onChange={(e) => {
                            setField(
                              "billingZip",
                              normalizeZip(e.target.value)
                            );
                            if (billingZipError) setBillingZipError("");
                          }}
                          maxLength={5}
                        />
                        {inputError(billingZipError)}
                      </div>
                    </div>
                  </>
                ) : null}

                {/* Payment */}
                <h3 className="nf12-formTitle">
                  <img
                    src={padlockIcon}
                    style={{ width: 15, height: 15, marginRight: 6 }}
                    alt=""
                  />
                  Payment Method
                </h3>

                {/* <div className="nf12-bankNote" style={{ marginBottom: 12 }}>
                  {pricingLoading
                    ? "Loading price..."
                    : priceId
                    ? `Charged today: $${displayPrice}`
                    : "Price unavailable"}
                </div> */}

                <div
                  className="account-detail-container"
                  style={{ padding: 0 }}
                >
                  <div className="input-field-placement">
                    <label
                      className="stripe-card-fields nf12-label"
                      style={{ lineHeight: "18px" }}
                    >
                      Card number
                      <div style={{ position: "relative" }}>
                        <CardNumberElement
                          options={elementStyle}
                          className="stripe-input nf12-input content-center"
                          onChange={(ev: any) => {
                            setIsCardNumberValid(!!ev.complete);
                            setCardNumberErr(ev?.error?.message || "");
                          }}
                        />
                        <img
                          src={creditCardIcon}
                          alt="Credit Card Icon"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "73px",
                            height: "20px",
                          }}
                          className="credit-card-icon"
                        />
                      </div>
                    </label>
                    {inputError(cardNumberErr)}
                  </div>

                  <div
                    className="state-zip-pacement"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <label
                      className="stripe-card-fields nf12-label"
                      style={{ lineHeight: "18px" }}
                    >
                      Expiration date
                      <CardExpiryElement
                        options={elementStyle}
                        className="stripe-input nf12-input content-center box-width"
                        onChange={(ev: any) => {
                          setIsCardExpiryValid(!!ev.complete);
                          setCardExpiryErr(ev?.error?.message || "");
                        }}
                      />
                      {inputError(cardExpiryErr)}
                    </label>

                    <div
                      className="input-field-placement"
                      style={{ position: "relative" }}
                    >
                      <label
                        className="stripe-card-fields nf12-label"
                        style={{ lineHeight: "18px" }}
                      >
                        Security code
                        <CardCvcElement
                          options={elementStyle}
                          className="stripe-input nf12-input content-center box-width"
                          onChange={(ev: any) => {
                            setIsCardCvcValid(!!ev.complete);
                            setCardCvcErr(ev?.error?.message || "");
                          }}
                        />
                      </label>
                      <img src={cvcIcon} alt="CVC Info" className="cvc-icon" />
                      {inputError(cardCvcErr)}
                    </div>
                  </div>

                  <div
                    className="state-zip-pacement"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <label
                      className="stripe-card-fields nf12-label"
                      style={{ lineHeight: "18px" }}
                    >
                      Country
                      <select
                        value={form.country}
                        onChange={(e) =>
                          setField("country", e.target.value as any)
                        }
                        className="custom-input nf12-input content-center box-width"
                      >
                        <option value="">Select Country</option>
                        <option value="US">United States</option>
                      </select>
                    </label>

                    <div
                      className="input-field-placement"
                      style={{ position: "relative" }}
                    >
                      <label
                        className="stripe-card-fields nf12-label"
                        style={{ lineHeight: "18px" }}
                      >
                        Postcode
                        <input
                          type="text"
                          value={form.postcode}
                          onChange={(e) =>
                            setField("postcode", normalizeZip(e.target.value))
                          }
                          className="custom-input nf12-input content-center box-width"
                          placeholder="Postcode"
                          maxLength={5}
                        />
                      </label>
                      <img
                        src={cvcIcon}
                        alt="Postcode Info"
                        className="cvc-icon"
                      />
                    </div>
                  </div>

                  <div className="checkbox-row" style={{ marginTop: 15 }}>
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={form.agreeTerms}
                      onChange={(e) => setField("agreeTerms", e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    <label htmlFor="agreeTerms" className="agree-text">
                      I AGREE TO THE{" "}
                      <a
                        href="https://claya.com/terms-of-use/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        TERMS OF USE
                      </a>
                      ,{" "}
                      <a
                        href="https://claya.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        PRIVACY POLICY
                      </a>
                      , AND{" "}
                      <a
                        href="https://openloophealth.com/telehealth-consent/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        TELEHEALTH CONSENT
                      </a>
                      .
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="pay-now-btn"
                    disabled={!canSubmit}
                    style={{
                      backgroundColor: !canSubmit ? "#d3d3d3" : "#0c3972",
                      cursor: !canSubmit ? "not-allowed" : "pointer",
                      color: "white",
                      marginTop: 16,
                    }}
                  >
                    <img
                      src={lockIcon}
                      alt="Lock Icon"
                      style={{
                        marginRight: 6,
                        verticalAlign: "middle",
                        width: 13,
                        height: 15,
                      }}
                    />
                    {loading ? "Processing..." : "Continue →"}
                  </button>

                  {isPaymentErr ? (
                    <p className="error-message">{isPaymentErr}</p>
                  ) : null}
                  {patientErr ? (
                    <p className="error-message">{patientErr}</p>
                  ) : null}
                </div>
              </form>
            </div>
          </section>

          {/* ✅ Testimonials + Stats section (NOT SKIPPED) */}
          <section className="nf12-card">
            <div className="nf12-guarantee">
              <div className="nf12-guaranteeIcon" aria-hidden="true">
                <img src={semaIconBadge} alt="semaIconBadge" />
              </div>
              <div>
                <div className="nf12-guaranteeTitle">Weight Loss Guarantee</div>
                <div className="nf12-guaranteeText">
                  If you do not lose weight by the end of your complete program,
                  we give you all of your money back. It’s that simple!
                </div>
              </div>
            </div>

            <div className="nf12-backedby">
              <img src={BackedBy} alt="backedby" />
            </div>

            <div className="nf12-statsCard">
              <h3 className="nf12-statsCard-title nf12-center">
                What makes CLAYA<span>so much better</span> than anything else?
              </h3>
              <div className="nf12-stats">
                {[
                  {
                    icon: SemaIconScale,
                    left: "18%",
                    right: "Average reduction in body weight",
                  },
                  {
                    icon: SemaIconTrophy,
                    left: "9/10",
                    right:
                      "Patients say it’s the most effective weight loss treatment ever tried",
                  },
                  {
                    icon: SemaIconRing,
                    left: "6.5″",
                    right: "Average reduction in waist size",
                  },
                  {
                    icon: SemaIconDown,
                    left: "93%",
                    right: "Patients have kept the weight off",
                  },
                ].map((s) => (
                  <div className="nf12-statRow" key={s.left}>
                    <img src={s.icon} alt={s.right} />
                    <div className="nf12-statLeft">{s.left}</div>
                    <div className="nf12-statRight">{s.right}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="nf12-testimonials-container">
              <h3 className="nf12-section-testimonials nf12-center">
                The <span>results </span>speak for themselves!
              </h3>
              <p>
                CLAYA success stories are pouring in, and we can not get enough.
              </p>
            </div>

            <div className="nf12-testimonials">
              {[
                {
                  title: "“CLAYA saved my life”",
                  quote:
                    "Trust me when I say that I have tried EVERYTHING under the sun to lose weight. I spent years struggling, but in just 5 months, I reached my goal weight thanks to you guys. I got my life back.",
                  name: "Katie R.",
                },
                {
                  title: "“The world looks at me differently now“",
                  quote:
                    "I have lost a total of 26 lbs in 3 months and I already notice people treating me with more respect. The progress has been steady and I am excited for what lies ahead.",
                  name: "Rachel G.",
                },
                {
                  title: "“I feel sexy again”",
                  quote:
                    "CLAYA treated me with such care and patience and helped me reach my goal weight. I gained my confidence again and I even fit in my wedding dress. I lost 45 pounds of baby weight in 4 months!",
                  name: "Shannon B.",
                },
              ].map((t) => (
                <div className="nf12-testimonial" key={t.name}>
                  <div className="nf12-testimonial-title">
                    <h3>{t.title}</h3>
                    <div className="nf12-testStars" aria-hidden="true">
                      ★★★★★
                    </div>
                  </div>

                  <div className="nf12-testQuote">“{t.quote}”</div>
                  <div className="nf12-testFooter">
                    <span className="nf12-testName">{t.name}</span>
                    <span className="nf12-verified">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_19_3840)">
                          <path
                            d="M16.9688 8C16.9688 9.15625 16.4062 10.1562 15.5 10.7188C15.7188 11.75 15.4375 12.875 14.625 13.6562C13.8438 14.4688 12.7188 14.7812 11.6875 14.5625C11.0938 15.4375 10.125 16 8.96875 16C7.84375 16 6.84375 15.4375 6.25 14.5625C5.21875 14.7812 4.125 14.4688 3.3125 13.6562C2.53125 12.875 2.21875 11.75 2.4375 10.7188C1.5625 10.1562 0.96875 9.15625 0.96875 8C0.96875 6.875 1.5625 5.875 2.4375 5.3125C2.21875 4.28125 2.53125 3.15625 3.3125 2.34375C4.125 1.5625 5.25 1.25 6.25 1.46875C6.84375 0.59375 7.84375 0 8.96875 0C10.125 0 11.0938 0.59375 11.6875 1.46875C12.7188 1.25 13.8438 1.5625 14.625 2.34375C15.4375 3.15625 15.7188 4.28125 15.5 5.3125C16.375 5.875 16.9688 6.875 16.9688 8ZM12 6.78125C12.3125 6.5 12.3125 6.03125 12 5.75C11.7188 5.4375 11.25 5.4375 10.9688 5.75L8 8.71875L7 7.75C6.71875 7.4375 6.25 7.4375 5.96875 7.75C5.65625 8.03125 5.65625 8.5 5.96875 8.78125L7.46875 10.2812C7.59375 10.4375 7.78125 10.5 8 10.5C8.1875 10.5 8.375 10.4375 8.5 10.2812L12 6.78125Z"
                            fill="#B18FFF"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_19_3840">
                            <rect width="17" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>{" "}
                      Verified customer
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <div className="nf12-ready-box">
            <div className="nf12-ready">
              <div className="nf12-readyTag">Are You Ready?</div>
              <div className="nf12-readyTitle">
                Start Your Journey <span>Today</span>
              </div>
              <div className="nf12-readySub">
                You’re approved for <span>{formatMMSS(approvalLeftSec)}</span>
              </div>
            </div>
            <div className="nf12-ready-right">
              <img src={Ozempic} alt="Ozempic" />
            </div>
          </div>

          {/* ✅ Legal/Footer section (NOT SKIPPED) */}
          <section className="nf12-card-happens-next nf12-card-finalprice">
            <div className="nf12-card-banner">
              Prescriptions start at just ${displayPrice} - no insurance needed
            </div>
            <div className="nf12-card-banner-header">
              The most effective weight loss program is <span>right here</span>
            </div>
            <div className="nf12-includedList-header">What is included?</div>
            <div className="nf12-includedList nf12-includedList-final">
              {[
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="23"
                      viewBox="0 0 17 23"
                      fill="none"
                    >
                      <path
                        d="M14.4375 7.625C14.7812 7.625 15.125 7.96875 15.125 8.3125V20C15.125 21.5469 13.8789 22.75 12.375 22.75H4.125C2.57812 22.75 1.375 21.5469 1.375 20V8.3125C1.375 7.96875 1.67578 7.625 2.0625 7.625C2.40625 7.625 2.75 7.96875 2.75 8.3125V9H6.1875C6.53125 9 6.875 9.34375 6.875 9.6875C6.875 10.0742 6.53125 10.375 6.1875 10.375H2.75V13.125H6.1875C6.53125 13.125 6.875 13.4688 6.875 13.8125C6.875 14.1992 6.53125 14.5 6.1875 14.5H2.75V17.25H6.1875C6.53125 17.25 6.875 17.5938 6.875 17.9375C6.875 18.3242 6.53125 18.625 6.1875 18.625H2.75V20C2.75 20.7734 3.35156 21.375 4.125 21.375H12.375C13.1055 21.375 13.75 20.7734 13.75 20V8.3125C13.75 7.96875 14.0508 7.625 14.4375 7.625ZM15.125 0.75C15.8555 0.75 16.5 1.39453 16.5 2.125V4.875C16.5 5.64844 15.8555 6.25 15.125 6.25H1.375C0.601562 6.25 0 5.64844 0 4.875V2.125C0 1.39453 0.601562 0.75 1.375 0.75H15.125ZM15.125 4.875V2.125H1.375V4.875H15.125Z"
                        fill="black"
                      />
                    </svg>
                  ),
                  title: "Access to GLP-1 Medication",
                  sub: "Cost of medication is included",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="21"
                      viewBox="0 0 24 21"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3886)">
                        <path
                          d="M17.8748 10.1875C18.3826 10.1875 18.8123 10.6172 18.8123 11.125C18.8123 11.6719 18.3826 12.0625 17.8748 12.0625C17.3279 12.0625 16.9373 11.6719 16.9373 11.125C16.9373 10.6172 17.3279 10.1875 17.8748 10.1875ZM22.2498 9.25C22.9139 9.25 23.4998 9.83594 23.4607 10.5V14.2891C23.4607 14.9922 22.8748 15.5391 22.2107 15.5391H20.9217C20.5701 16.0078 20.1404 16.4375 19.7107 16.7891V18.6641C19.7107 19.6797 18.8514 20.5 17.8357 20.5H15.9607C14.9451 20.5 14.0857 19.6797 14.0467 18.625V18H11.5467V18.6641C11.5467 19.6797 10.7264 20.5 9.71073 20.5H7.83573C6.8201 20.5 5.96073 19.6797 5.96073 18.625V16.75C4.47635 15.6172 3.46073 13.8203 3.46073 11.75H3.14823C1.85917 11.75 0.843539 10.6172 0.999789 9.28906C1.11698 8.35156 1.8201 7.64844 2.71854 7.45312C3.10917 7.375 3.46073 7.64844 3.46073 8.07812C3.46073 8.35156 3.26541 8.625 2.99198 8.66406C2.56229 8.74219 2.32791 9.05469 2.24979 9.40625C2.1326 9.99219 2.60135 10.5 3.14823 10.5H3.57791C3.81229 9.25 4.43729 8.11719 5.33573 7.25781C6.46854 6.16406 8.0701 5.5 9.71073 5.5H15.5701C15.8045 5.34375 17.2498 4.28906 19.0857 4.28906L19.7498 4.25V6.78906C20.5701 7.45312 21.2732 8.27344 21.7029 9.25H22.2498ZM22.2107 14.25V10.5H20.8826C20.1795 8.9375 20.0232 8.58594 18.4607 7.41406V5.57812C17.3279 5.73438 16.6639 6.24219 15.9998 6.75H9.71073C6.97635 6.75 4.71073 9.01562 4.71073 11.75C4.71073 12.4531 4.71073 14.2109 7.21073 16.125V18.625C7.21073 18.9766 7.48417 19.25 7.83573 19.25H9.71073C10.0232 19.25 10.3357 18.9766 10.3357 18.625V16.75H15.3357V18.625C15.3357 18.9766 15.6092 19.25 15.9607 19.25H17.8357C18.1482 19.25 18.4607 18.9766 18.4607 18.625V16.125C19.6326 15.2266 19.6326 15.1484 20.2967 14.25H22.2107ZM7.7576 4.875C7.40604 4.83594 7.17167 4.52344 7.24979 4.17188C7.60135 2.0625 9.47635 0.5 11.7029 0.5C13.617 0.5 15.3357 1.71094 15.9607 3.46875C16.0779 3.78125 15.8826 4.13281 15.531 4.25C15.2186 4.36719 14.8279 4.17188 14.7498 3.85938C14.281 2.60938 13.0701 1.75 11.7029 1.75C10.1014 1.75 8.77323 2.84375 8.49979 4.36719C8.46073 4.67969 8.18729 4.875 7.87479 4.875C7.83573 4.875 7.79667 4.875 7.7576 4.875Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3886">
                          <rect width="24" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "No insurance necessary",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="21"
                      viewBox="0 0 13 21"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3891)">
                        <path
                          d="M10.25 0.5C11.6172 0.5 12.75 1.63281 12.75 3V18C12.75 19.4062 11.6172 20.5 10.25 20.5H2.75C1.34375 20.5 0.25 19.4062 0.25 18V3C0.25 1.63281 1.34375 0.5 2.75 0.5H10.25ZM5.25 1.75V2.375H7.75V1.75H5.25ZM11.5 18V3C11.5 2.33594 10.9141 1.75 10.25 1.75H9V2.375C9 3.07812 8.41406 3.625 7.75 3.625H5.25C4.54688 3.625 4 3.07812 4 2.375V1.75H2.75C2.04688 1.75 1.5 2.33594 1.5 3V18C1.5 18.7031 2.04688 19.25 2.75 19.25H10.25C10.9141 19.25 11.5 18.7031 11.5 18ZM7.75 16.125C8.0625 16.125 8.375 16.4375 8.375 16.75C8.375 17.1016 8.0625 17.375 7.75 17.375H5.25C4.89844 17.375 4.625 17.1016 4.625 16.75C4.625 16.4375 4.89844 16.125 5.25 16.125H7.75Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3891">
                          <rect width="13" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "Board-Certified doctor review",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3896)">
                        <path
                          d="M20.25 7.99957C20.25 9.17145 19.4297 10.148 18.375 10.4215V14.2496C18.375 17.648 15.5625 20.4605 12.125 20.4605C8.64844 20.4605 5.875 17.6871 5.875 14.2496V14.1715C2.71094 13.859 0.25 11.2027 0.25 7.99957V2.41363C0.25 2.14019 0.367188 1.94488 0.5625 1.78863L3.0625 0.577697C3.375 0.421447 3.76562 0.538634 3.92188 0.851134C4.07812 1.16363 3.96094 1.55426 3.64844 1.71051L1.5 2.76519V7.99957C1.5 10.773 3.72656 12.9996 6.5 12.9996C9.23438 12.9996 11.5 10.773 11.5 7.99957V2.76519L9.3125 1.71051C9 1.55426 8.88281 1.16363 9.03906 0.92926C9.19531 0.61676 9.58594 0.499572 9.89844 0.655822L12.3984 1.86676C12.5938 1.98395 12.75 2.17926 12.75 2.41363V7.99957C12.75 11.2418 10.25 13.898 7.125 14.2105V14.2496C7.125 16.984 9.35156 19.2496 12.125 19.2496C14.8594 19.2496 17.125 17.023 17.125 14.2496V10.4215C16.0312 10.148 15.25 9.17145 15.25 7.99957C15.25 6.67145 16.3438 5.53863 17.75 5.53863C19.1172 5.53863 20.25 6.63238 20.25 7.99957ZM17.75 9.24957C18.4141 9.24957 19 8.70269 19 8.03863C19 7.41363 18.4141 6.82769 17.75 6.82769C17.0469 6.82769 16.5 7.37457 16.5 8.03863C16.5 8.70269 17.0469 9.24957 17.75 9.24957Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3896">
                          <rect width="21" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "1:1 Physician guidance",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="21"
                      viewBox="0 0 15 21"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3901)">
                        <path
                          d="M4.375 5.5C4.02344 5.5 3.75 5.22656 3.75 4.875C3.75 4.5625 4.02344 4.25 4.375 4.25H5.3125C5.11719 3.89844 5 3.46875 5 3C5 1.63281 6.09375 0.5 7.5 0.5C8.86719 0.5 10 1.63281 10 3C10 3.46875 9.84375 3.89844 9.64844 4.25H10.625C10.9375 4.25 11.25 4.5625 11.25 4.875C11.25 5.22656 10.9375 5.5 10.625 5.5H4.375ZM7.5 1.75C6.79688 1.75 6.25 2.33594 6.25 3C6.25 3.70312 6.79688 4.25 7.5 4.25C8.16406 4.25 8.75 3.70312 8.75 3C8.75 2.33594 8.16406 1.75 7.5 1.75ZM12.5 3C13.8672 3 15 4.13281 15 5.5V18C15 19.4062 13.8672 20.5 12.5 20.5H2.5C1.09375 20.5 0 19.4062 0 18V5.5C0 4.13281 1.09375 3 2.5 3C2.8125 3 3.125 3.3125 3.125 3.625C3.125 3.97656 2.8125 4.25 2.5 4.25C1.79688 4.25 1.25 4.83594 1.25 5.5V18C1.25 18.7031 1.79688 19.25 2.5 19.25H12.5C13.1641 19.25 13.75 18.7031 13.75 18V5.5C13.75 4.83594 13.1641 4.25 12.5 4.25C12.1484 4.25 11.875 3.97656 11.875 3.625C11.875 3.3125 12.1484 3 12.5 3ZM4.375 13.625C4.02344 13.625 3.75 13.3516 3.75 13V11.75C3.75 11.4375 4.02344 11.125 4.375 11.125H6.25V9.25C6.25 8.9375 6.52344 8.625 6.875 8.625H8.125C8.4375 8.625 8.75 8.9375 8.75 9.25V11.125H10.625C10.9375 11.125 11.25 11.4375 11.25 11.75V13C11.25 13.3516 10.9375 13.625 10.625 13.625H8.75V15.5C8.75 15.8516 8.4375 16.125 8.125 16.125H6.875C6.52344 16.125 6.25 15.8516 6.25 15.5V13.625H4.375Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3901">
                          <rect width="15" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "Metabolic report",
                  sub: "Includes custom in-depth metabolic results",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3907)">
                        <path
                          d="M19.6859 11.0996C20.0375 11.0996 20.35 11.4121 20.3109 11.7637C20.0766 14.7324 18.0453 17.1934 15.3109 18.1699V18.5996C15.3109 19.3027 14.8031 19.8496 14.1 19.8496H6.59999C5.89687 19.8496 5.34999 19.3027 5.34999 18.5996V18.1699C2.61561 17.2324 0.58437 14.7715 0.34999 11.7637C0.310928 11.4121 0.62343 11.0996 0.97499 11.0996H19.6859ZM14.9203 16.998C17.0297 16.2558 18.5531 14.498 18.9828 12.3496H1.67811C2.1078 14.498 3.6703 16.2558 5.74061 16.998L6.59999 17.2715V18.5996H14.0609V17.2715L14.9203 16.998ZM3.63124 5.66992C3.86561 5.43555 4.2953 5.43555 4.52968 5.66992L7.65468 8.79492C7.88905 9.0293 7.88905 9.45901 7.65468 9.69341C7.53749 9.81051 7.38124 9.84961 7.22499 9.84961C7.02968 9.84961 6.87343 9.81051 6.75624 9.69341L3.63124 6.56836C3.39687 6.33399 3.39687 5.9043 3.63124 5.66992ZM9.09999 4.22461C9.09999 3.91211 9.37339 3.59961 9.72499 3.59961C10.0375 3.59961 10.35 3.91211 10.35 4.22461V9.22461C10.35 9.57621 10.0375 9.84961 9.72499 9.84961C9.37339 9.84961 9.09999 9.57621 9.09999 9.22461V4.22461ZM11.6 9.22461C11.6 6.8418 13.5531 4.84961 15.975 4.84961C18.3578 4.84961 20.35 6.8418 20.35 9.26367C20.35 9.57621 20.0375 9.84961 19.725 9.84961C19.3734 9.84961 19.1 9.57621 19.1 9.22461C19.1 7.50586 17.6938 6.09961 15.975 6.09961C14.2172 6.09961 12.85 7.50586 12.85 9.22461C12.85 9.57621 12.5375 9.84961 12.225 9.84961C11.8734 9.84961 11.6 9.57621 11.6 9.22461ZM1.36561 9.84961C1.13124 9.84961 0.93593 9.73241 0.81874 9.49801C0.50624 8.83399 0.34999 8.0918 0.34999 7.34961C0.34999 4.57617 2.57655 2.34961 5.34999 2.34961C5.42811 2.34961 5.50624 2.34961 5.58437 2.34961C6.40468 0.826169 7.96718 -0.150391 9.72499 -0.150391C11.4438 -0.150391 13.0062 0.826169 13.8266 2.34961C14.6859 2.27149 15.4672 2.4668 16.2094 2.81836C16.5219 2.97461 16.6781 3.36523 16.5219 3.67773C16.3656 3.99023 16.0141 4.10742 15.7016 3.95117C15.0375 3.63867 14.2953 3.52149 13.5922 3.63867L13.1234 3.7168L12.8891 3.24805C12.3422 1.95899 11.0922 1.09961 9.72499 1.09961C8.31874 1.09961 7.06874 1.95899 6.52187 3.24805L6.36561 3.7168L5.8578 3.63867C5.66249 3.63867 5.50624 3.59961 5.34999 3.59961C3.27968 3.59961 1.59999 5.2793 1.59999 7.34961C1.59999 7.93555 1.71718 8.48242 1.95155 8.99023C2.1078 9.30273 1.95155 9.65431 1.63905 9.81051C1.56093 9.84961 1.4828 9.84961 1.36561 9.84961Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3907">
                          <rect
                            width="21"
                            height="21"
                            fill="white"
                            transform="translate(-0.649902 -0.650391)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "Sample meal plan based on metabolism",
                },
                {
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="21"
                      viewBox="0 0 18 21"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19_3912)">
                        <path
                          d="M17.5 14.875C17.5 15.6953 16.9531 16.3984 16.25 16.6719V19.25H16.875C17.1875 19.25 17.5 19.5625 17.5 19.875C17.5 20.2266 17.1875 20.5 16.875 20.5H2.5C1.09375 20.5 0 19.4062 0 18V3.625C0 1.90625 1.36719 0.5 3.125 0.5H15.625C16.6406 0.5 17.5 1.35938 17.5 2.375V14.875ZM1.25 3.625V15.8516C1.60156 15.6562 2.03125 15.5 2.5 15.5H3.75V1.75H3.125C2.07031 1.75 1.25 2.60938 1.25 3.625ZM15 19.25V16.75H2.5C1.79688 16.75 1.25 17.3359 1.25 18C1.25 18.7031 1.79688 19.25 2.5 19.25H15ZM15.625 15.5C15.9375 15.5 16.25 15.2266 16.25 14.875V2.375C16.25 2.0625 15.9375 1.75 15.625 1.75H5V15.5H15.625ZM13.75 5.5C14.0625 5.5 14.375 5.8125 14.375 6.125C14.375 6.47656 14.0625 6.75 13.75 6.75H7.5C7.14844 6.75 6.875 6.47656 6.875 6.125C6.875 5.8125 7.14844 5.5 7.5 5.5H13.75ZM13.75 9.25C14.0625 9.25 14.375 9.5625 14.375 9.875C14.375 10.2266 14.0625 10.5 13.75 10.5H7.5C7.14844 10.5 6.875 10.2266 6.875 9.875C6.875 9.5625 7.14844 9.25 7.5 9.25H13.75Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19_3912">
                          <rect width="18" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ),
                  title: "Nutrition report",
                },
              ].map((item) => (
                <div className="nf12-includeRow2" key={item.title}>
                  <span className="nf12-includeIconSvg" aria-hidden="true">
                    {item.icon}
                  </span>

                  <div>
                    <span className="nf12-includeTitle">{item.title}</span>

                    {item.sub && (
                      <div className="nf12-includeSubText">{item.sub}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="nf12-finalPrice">
              <div className="nf12-finalLine">
                {activeMed.name} prescribed for
              </div>
              <div className="nf12-finalBig">just ${displayPrice}</div>
              <div className="nf12-finalTiny">
                {" "}
                Pay one month at a time. No contracts. Cancel anytime.{" "}
                <p>
                  Medication <span>is included.</span>
                </p>
              </div>
            </div>

            <div className="nf12-button-container">
              <button
                type="button"
                className="nf12-checkoutBtn"
                onClick={() => {
                  checkoutFormRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }}
              >
                CHECKOUT →
              </button>
            </div>

            <div className="nf12-guarantee" style={{ padding: "40px 32px" }}>
              <div
                className="nf12-guaranteeIcon"
                aria-hidden="true"
                style={{ marginRight: "10px" }}
              >
                <img src={semaIconBadge} alt="semaIconBadge" />
              </div>
              <div>
                <div className="nf12-guaranteeTitle">Weight Loss Guarantee</div>
                <div className="nf12-guaranteeText">
                  If you do not lose weight by the end of your complete program,
                  we give you all of your money back. It’s that simple!
                </div>
              </div>
            </div>
          </section>
          <div className="nf12-faqCard">
            <h3 className="nf12-faqTitle">Frequently Asked Questions</h3>

            {[
              {
                q: "What is the CLAYA Prescription Plan?",
                a: "CLAYA Prescription Plan is a weight loss plan that includes a personalized meal program PLUS telehealth doctor support and access to weight loss medications through a written prescription which can be used to fill at a local pharmacy or to purchase medication directly through CLAYA for a discounted price.",
              },
              {
                q: "What is the cost of medication?",
                a: "If you choose to purchase the medication through CLAYA via self-pay (no insurance) the cost for your Semaglutide prescription is only $179 for your first month. Refill price is locked in at $299, regardless of the dosage.",
              },
              {
                q: "Is it difficult to take the meds / what if I don’t know how to do the injections?",
                a: "If you choose dissolvable tablets, the medicine dissolves right on your tongue. For injections, it’s simple and painless. Injections just take a few seconds and become second nature within weeks.",
              },
            ].map((item, index) => {
              const isOpen = openIndex === index;

              return (
                <div key={item.q}>
                  <button
                    type="button"
                    className="nf12-faqItem"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                  >
                    <span
                      className={`nf12-faqIcon ${isOpen ? "open" : ""}`}
                      aria-hidden="true"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4.6875 3.4375L9.01562 7.76562L4.6875 12.1094L6.125 13.5469L12.1875 7.78125L6.125 2L4.6875 3.4375Z"
                          fill="#0C3972"
                        />
                      </svg>
                    </span>

                    <span style={{ width: "280px" }}>{item.q}</span>
                  </button>

                  {isOpen && <div className="nf12-faqAnswer">{item.a}</div>}
                </div>
              );
            })}
          </div>

          <div className="nf12-legal">
            <p>
              Wegovy is FDA-approved for weight loss. Ozempic is FDA-approved
              for type 2 diabetes treatment, but may be prescribed for weight
              loss.
            </p>
            <span>
              *Results vary based on starting weight and program adherence.
              Inches lost <br /> from hips, waist, chest, thighs and arms in the
              first month. These patients exercised daily and ate a
              reduced-calorie diet. Their fat loss <br /> is <br /> not typical.
              <br /> Your results may vary. Medication prescriptions are at the
              discretion of medical providers and may not be suitable for
              everyone. CLAYA patients typically result in 1-2 lbs per week
              weight loss in 4 weeks, involving a healthy diet and exercise
              changes. Consult a healthcare professional before using <br />{" "}
              medication or starting any weight loss program.
              <br /> *Based on the average weight loss in three 68-week clinical
              trials of patients without diabetes who reached and maintained a
              dose of 2.4 mg/week of GLP-1 treatment, along with a
              reduced-calorie diet and increased physical <br />
              activity.{" "}
              <a
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9486455/"
                target="_blank"
                rel="noreferrer"
              >
                See details.
              </a>
              <br />
              Medication is included in the cost of the CLAYA Program. Wegovy®
              is FDA-approved for weight loss. Ozempic® is FDA-approved for type
              2 diabetes treatment but may be prescribed for weight loss. The
              trademarks, service marks, trade names (Wegovy® ,Ozempic®), and
              products displayed on this Internet site are protected and belong
              to their respetive <br /> owners. <br /> Medical treatment is
              provided by “CLAYA Affiliated P.C.s,” an affiliated <br />
              network for medical professional corporations and associations.
            </span>
          </div>
          <div className="nf12-trustBadge">
            <img src={TrustBadge} alt="trustbadge" />
          </div>
        </div>
      </div>
    </FunnelLayout>
  );
};

const NewFunnelStep12: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <NewFunnelStep12Inner />
    </Elements>
  );
};

export default NewFunnelStep12;
